import type { DeviceInfo } from "./session-recorder";

/** Not in `lib.dom` yet, and only the two fields worth asking for. */
interface NavigatorUAData {
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string; platformVersion?: string }>;
}

export interface DeviceNavigator {
  userAgent: string;
  userAgentData?: NavigatorUAData;
  maxTouchPoints: number;
  hardwareConcurrency?: number;
  deviceMemory?: number;
}

/**
 * Which operating system a user agent describes, or null when it is not one we can name.
 *
 * Only consulted when the browser refuses to say — which is every Safari, since UA Client Hints is
 * Chromium-only. Without it half the fleet arrives unlabelled and the readings cannot be split by
 * platform, which is the first cut anyone makes when a tracker behaves differently on iOS.
 *
 * No new data leaves the device: this reads the user-agent string that is already reported.
 */
export function platformFromUserAgent(ua: string, maxTouchPoints: number): string | null {
  if (ua.includes("iPhone")) return "iOS";
  if (ua.includes("iPad")) return "iPadOS";
  // Safari on iPadOS requests desktop sites by default and calls itself a Mac. A Mac reports no
  // touch points, so that is the only thing in reach that separates them.
  if (ua.includes("Macintosh")) return maxTouchPoints > 0 ? "iPadOS" : "macOS";
  if (ua.includes("Android")) return "Android";
  if (ua.includes("Windows")) return "Windows";
  return null;
}

/**
 * Device *class*, never identity: enough to say "this failed on an S24 Ultra at 3x" and nothing
 * that could pick a person out. Chromium answers `model` through UA-CH; Safari does not, so an iPad
 * is separated from the Mac it claims to be by touch points and screen size instead.
 */
export async function describeDevice(
  nav: DeviceNavigator,
  screen: { width: number; height: number },
  dpr: number,
): Promise<DeviceInfo> {
  const highEntropy = await nav.userAgentData
    ?.getHighEntropyValues?.(["model", "platformVersion"])
    .catch(() => undefined);

  return {
    ua: nav.userAgent,
    model: highEntropy?.model ?? null,
    // What the browser reports wins; the string is only read when it will not answer.
    platform: nav.userAgentData?.platform ?? platformFromUserAgent(nav.userAgent, nav.maxTouchPoints),
    platformVersion: highEntropy?.platformVersion ?? null,
    maxTouchPoints: nav.maxTouchPoints,
    screen: { width: screen.width, height: screen.height },
    dpr,
    cores: nav.hardwareConcurrency ?? null,
    memoryGb: nav.deviceMemory ?? null,
  };
}
